import React from 'react';
import { Box, Card, CardContent, Typography, IconButton, Button, Alert, CircularProgress } from '@mui/material';
import { CheckCircle, Cancel, Upload, Delete } from '@mui/icons-material';
import { DocumentUpload, FormData } from './types';

interface DocumentUploadFieldProps {
  documentKey: keyof FormData;
  label: string;
  required?: boolean;
  formData: FormData;
  uploadedDocs: { [key: string]: any };
  pendingUpload: { key: keyof FormData, file: File } | null;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, documentKey: keyof FormData) => void;
  removeFile: (documentKey: keyof FormData) => void;
  confirmAndUploadFile: () => void;
  setPendingUpload: (value: any) => void;
}

const getStatusIcon = (status: DocumentUpload["status"]) => {
  switch (status) {
    case "uploading":
      return <CircularProgress size={20} />;
    case "success":
      return <CheckCircle sx={{ color: '#00C853' }} />;
    case "error":
      return <Cancel color="error" />;
    default:
      return <Upload color="action" />;
  }
};

const DocumentUploadField: React.FC<DocumentUploadFieldProps> = ({
  documentKey,
  label,
  required = true,
  formData,
  uploadedDocs,
  pendingUpload,
  handleFileChange,
  removeFile,
  confirmAndUploadFile,
  setPendingUpload,
}) => {
  const document = formData[documentKey] as DocumentUpload;
  const uploadedDoc = uploadedDocs[documentKey];

  return (
    <Box component="span" display="block">
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {label}
              {required && <span style={{ color: "red" }}> *</span>}
            </Typography>
            {uploadedDoc ? (
              <CheckCircle sx={{ color: '#00C853' }} />
            ) : (
              getStatusIcon(document.status)
            )}
          </Box>
          {uploadedDoc && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                <a href={uploadedDoc.s3_url} target="_blank" rel="noopener noreferrer">
                  View Uploaded Document
                </a>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Status: {uploadedDoc.kyb_status}
              </Typography>
            </Box>
          )}
          {document.file && !uploadedDoc && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {document.file.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => removeFile(documentKey)}
                color="error"
              >
                <Delete />
              </IconButton>
            </Box>
          )}
          {document.status === "error" && document.errorMessage && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {document.errorMessage}
            </Alert>
          )}
          <Button
            variant="outlined"
            component="label"
            fullWidth
            disabled={document.status === "uploading" || !!uploadedDoc}
          >
            {uploadedDoc ? "Document Uploaded" : document.file ? "Change File" : "Choose File"}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => handleFileChange(e, documentKey)}
            />
          </Button>
          {uploadedDoc && (
            <Typography variant="caption" color="success.main">
              Document already uploaded. You can view it above.
            </Typography>
          )}
          {pendingUpload && pendingUpload.key === documentKey && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Selected file: {pendingUpload.file.name}
              </Typography>
              {/* Image preview */}
              {pendingUpload.file.type.startsWith('image/') && (
                <img
                  src={URL.createObjectURL(pendingUpload.file)}
                  alt="Preview"
                  style={{ maxWidth: 200, marginTop: 8 }}
                />
              )}
              {/* PDF preview */}
              {pendingUpload.file.type === 'application/pdf' && (
                <embed
                  src={URL.createObjectURL(pendingUpload.file)}
                  type="application/pdf"
                  width="100%"
                  height="400px"
                />
              )}
              {/* For other types, just show file name */}
              <Button
                variant="contained"
                color="primary"
                onClick={confirmAndUploadFile}
                sx={{ mt: 1 }}
              >
                Confirm & Upload
              </Button>
              <Button
                variant="text"
                color="secondary"
                onClick={() => setPendingUpload(null)}
                sx={{ mt: 1, ml: 2 }}
              >
                Cancel
              </Button>
            </Box>
          )}
          {uploadedDoc && (
            <Button
              variant="outlined"
              component="label"
              fullWidth
              disabled={document.status === "uploading"}
            >
              Replace File
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, documentKey)}
              />
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DocumentUploadField; 